import { memo, useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import MathText from '../MathText';
import { useQuizTheme, answerTheme, marqueSigne } from './quizTheme';
import { etatsRelier, ETIQUETTE_CONTENU } from './corrige';

// Couleur par paire (hex) — pour les traits SVG ET la bordure des éléments reliés.
// Tons "600" : lisibles aussi bien sur carte blanche que sur fond sombre.
const PAIR_HEX = ['#2563EB', '#059669', '#D97706', '#7C3AED', '#E11D48', '#0891B2'];
// Correction : le trait d'une paire juste, le ton des classes juste d'answerTheme.
const TRAIT_JUSTE = '#10B981';

/**
 * Composant interactif "Relier" côté élève.
 * Tap source → highlight → tap cible → paire créée. Un TRAIT coloré relie
 * visuellement chaque paire (overlay SVG). Tap un élément relié → on défait.
 *
 * @param {Array} pairs - [{ source, target }, ...] (format contenu.pairs)
 * @param {Array} ciblesLeurres - [{ target, code }, ...] cibles SURNUMÉRAIRES
 *   (aucune source ne leur correspond) — mélangées avec les vraies cibles.
 *   La complétude reste indexée sur les sources : les leurres peuvent (doivent)
 *   rester non reliés. Absent/vide = comportement historique inchangé.
 * @param {string} selectedValue - Réponse existante (JSON stringifié) pour restauration
 * @param {Function} onAnswer - Callback (JSON stringifié des paires reliées)
 * @param {Object|null} corrige - Correction sur place (lot 5, 2026-09-21), voir plus bas
 */
const RelierEleve = memo(function RelierEleve({ pairs, ciblesLeurres, selectedValue, onAnswer, onComplete, corrige = null }) {
    const clair = useQuizTheme() === 'clair';
    const t = answerTheme(clair);
    const sources = useMemo(() => (pairs || []).map(p => p.source), [pairs]);

    // ─── Correction sur place (lot 5, 2026-09-21) ───
    // `corrige` n'arrive que si la réponse est fausse (contrat dans corrige.jsx).
    // Par source : la cible qu'il a reliée, la cible attendue, juste ou faux.
    // Une paire juste : ses deux boîtes en vert avec une coche. Une paire
    // fausse : la source en rouge avec une croix, et la cible qu'il fallait en
    // pointillé vert, avec une étiquette qui nomme la source (« ← Carré »). Un
    // leurre relié à tort passe en rouge. Les traits : SEULS ses liens justes
    // restent, en vert ; un lien faux disparaît, et rien ne trace celui qu'il
    // fallait (la première version traçait son trait faux en rouge et le bon
    // en pointillé, illisible à l'écran, constat prof 2026-09-21). Ce qu'il
    // voit ressemble à ce qu'il avait sous les yeux : les traits qui restent
    // sont bons, les boîtes de droite disent où allaient les autres. Tout se
    // fige, la consigne et le compteur disparaissent. Sans `corrige`, le rendu
    // d'avant.
    const etats = useMemo(() => (corrige ? etatsRelier(corrige) : null), [corrige]);

    // Mélange des cibles gelé sur le CONTENU (ceinture du fix 2026-08-01) :
    // la clé est l'empreinte des textes des cibles — tant qu'elle ne change
    // pas (même question), l'ordre mélangé est conservé quels que soient les
    // re-rendus du parent ; seule une vraie nouvelle question re-mélange.
    const cleContenu = JSON.stringify([
        (pairs || []).map(p => p.target),
        (ciblesLeurres || []).map(l => l.target),
    ]);
    const melangeRef = useRef(null);
    if (melangeRef.current === null || melangeRef.current.cle !== cleContenu) {
        const targets = [
            ...(pairs || []).map(p => p.target),
            ...(ciblesLeurres || []).map(l => l.target),
        ];
        const shuffled = [...targets];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        melangeRef.current = { cle: cleContenu, valeurs: shuffled };
    }
    const shuffledTargets = melangeRef.current.valeurs;

    // State : liens créés { sourceIndex → targetIndex }
    const [links, setLinks] = useState(() => {
        if (selectedValue) {
            try {
                const parsed = JSON.parse(selectedValue);
                if (Array.isArray(parsed)) {
                    const restored = {};
                    for (const [src, tgt] of parsed) {
                        const si = sources.indexOf(src);
                        const ti = shuffledTargets.indexOf(tgt);
                        if (si !== -1 && ti !== -1) restored[si] = ti;
                    }
                    return restored;
                }
            } catch { /* ignore */ }
        }
        return {};
    });
    const [selectedSource, setSelectedSource] = useState(null);
    const [selectedTarget, setSelectedTarget] = useState(null);

    // Pas de resync sur selectedValue : l'état est lu UNE FOIS au montage,
    // le montage est keyé par question (Quiz / ExerciseurPreview).

    // Notifier le parent uniquement quand toutes les paires sont reliées
    const notifyAnswer = useCallback((currentLinks) => {
        const isComplete = Object.keys(currentLinks).length === sources.length;
        if (isComplete) {
            const result = Object.entries(currentLinks).map(([si, ti]) => [sources[si], shuffledTargets[ti]]);
            onAnswer(JSON.stringify(result));
        }
        onComplete(isComplete);
    }, [sources, shuffledTargets, onAnswer, onComplete]);

    // Ordre des liens (pour attribuer les couleurs de façon stable)
    const linkOrder = useMemo(() => {
        return Object.keys(links).map(Number).sort((a, b) => a - b);
    }, [links]);

    // Couleur (hex) par source/cible reliée — partagée entre le trait et la bordure
    const linkColors = useMemo(() => {
        const bySource = {};
        const byTarget = {};
        linkOrder.forEach((si, order) => {
            const c = PAIR_HEX[order % PAIR_HEX.length];
            bySource[si] = c;
            byTarget[links[si]] = c;
        });
        return { bySource, byTarget };
    }, [linkOrder, links]);

    // En correction : l'état de chaque cible, et les sources (fausses) qui
    // l'attendaient, pour l'étiquette. Une cible attendue prime sur une cible
    // où il est allé à tort : le trait rouge y arrive, la boîte dit à qui elle
    // était.
    const etatsCibles = useMemo(() => {
        if (!etats) return null;
        return shuffledTargets.map((target) => {
            const attendPar = [];
            let juste = false;
            let faux = false;
            for (const [source, e] of etats) {
                if (e.attendue === target) {
                    if (e.juste) juste = true; else attendPar.push(source);
                } else if (!e.juste && e.cible === target) {
                    faux = true;
                }
            }
            const etat = attendPar.length ? 'attendu' : juste ? 'juste' : faux ? 'faux' : null;
            return { etat, attendPar };
        });
    }, [etats, shuffledTargets]);

    // Trouver quel source est lié à un target
    const getSourceForTarget = useCallback((targetIdx) => {
        return Object.entries(links).find(([, ti]) => ti === targetIdx)?.[0];
    }, [links]);

    // ─── Créer OU DÉPLACER un lien entre source et cible ───
    // Réassignation (polish 2026-07-27) : si la cible appartenait à une autre
    // source, cette liaison est défaite d'abord — une cible n'a jamais deux
    // sources, par construction. notifyAnswer est rappelé à chaque mutation :
    // un déplacement sur état complet ré-émet la réponse à jour.
    // La nouvelle table de liens se calcule ICI, dans le gestionnaire, et c'est
    // la même valeur qu'on donne à setState et au parent, juste après. Jamais
    // dans la fonction passée à setState : React se réserve de la rejouer, et
    // chaque rejeu prévenait Quiz une fois de plus (boucle vécue sur l'horloge
    // le 21/09, corrigée ici le même jour).
    const createLink = useCallback((sourceIdx, targetIdx) => {
        const next = { ...links };
        for (const s of Object.keys(next)) {
            if (next[s] === targetIdx && Number(s) !== sourceIdx) delete next[s];
        }
        next[sourceIdx] = targetIdx;
        setLinks(next);
        notifyAnswer(next);
        setSelectedSource(null);
        setSelectedTarget(null);
    }, [links, notifyAnswer]);

    // ─── Défaire un lien ───
    const removeLink = useCallback((sourceIdx) => {
        const next = { ...links };
        delete next[sourceIdx];
        setLinks(next);
        notifyAnswer(next);
        setSelectedSource(null);
        setSelectedTarget(null);
    }, [links, notifyAnswer]);

    // ─── Machine à états des taps (re-lien direct, polish 2026-07-27) ───
    // Un élément RELIÉ se sélectionne au lieu de se détruire ; le lien se
    // défait en re-tapant l'élément sélectionné ou son partenaire ; il se
    // DÉPLACE en tapant une autre cible/source.
    const handleSourceClick = useCallback((sourceIdx) => {
        const estRelie = links[sourceIdx] !== undefined;
        if (selectedTarget !== null) {
            // Cible sélectionnée + tap sur SA source partenaire → défaire
            if (estRelie && links[sourceIdx] === selectedTarget) {
                removeLink(sourceIdx);
                return;
            }
            createLink(sourceIdx, selectedTarget);
            return;
        }
        if (selectedSource === sourceIdx) {
            // Re-tap sur soi : relié → défaire ; libre → désélection
            if (estRelie) { removeLink(sourceIdx); return; }
            setSelectedSource(null);
            return;
        }
        setSelectedSource(sourceIdx);
        setSelectedTarget(null);
    }, [links, selectedTarget, selectedSource, createLink, removeLink]);

    const handleTargetClick = useCallback((targetIdx) => {
        const sourceLiee = getSourceForTarget(targetIdx);
        if (selectedSource !== null) {
            // Source sélectionnée + tap sur SA cible partenaire → défaire
            if (links[selectedSource] === targetIdx) {
                removeLink(selectedSource);
                return;
            }
            // Cible libre → lier/déplacer ; cible reliée → réassigner (createLink)
            createLink(selectedSource, targetIdx);
            return;
        }
        if (selectedTarget === targetIdx) {
            if (sourceLiee !== undefined) { removeLink(Number(sourceLiee)); return; }
            setSelectedTarget(null);
            return;
        }
        setSelectedTarget(targetIdx);
        setSelectedSource(null);
    }, [selectedSource, selectedTarget, links, getSourceForTarget, createLink, removeLink]);

    // ─── Traits de liaison (overlay SVG) ───
    const containerRef = useRef(null);
    const sourceRefs = useRef({});
    const targetRefs = useRef({});
    const [segments, setSegments] = useState([]);

    // Les traits à tracer : les liens de l'élève, un ton par lien ; en
    // correction, seuls ses liens justes, en vert.
    const traits = useMemo(() => {
        if (etats) {
            const out = [];
            sources.forEach((source, si) => {
                const e = etats.get(source);
                if (!e || !e.juste) return;
                const ti = shuffledTargets.indexOf(e.cible);
                if (ti !== -1) out.push({ si, ti, color: TRAIT_JUSTE });
            });
            return out;
        }
        return linkOrder.map((si, order) => ({ si, ti: links[si], color: PAIR_HEX[order % PAIR_HEX.length] }));
    }, [etats, sources, shuffledTargets, linkOrder, links]);

    const computeSegments = useCallback(() => {
        const cont = containerRef.current;
        if (!cont) return;
        const cb = cont.getBoundingClientRect();
        const segs = [];
        traits.forEach(({ si, ti, color }, order) => {
            const sEl = sourceRefs.current[si];
            const tEl = targetRefs.current[ti];
            if (!sEl || !tEl) return;
            const sr = sEl.getBoundingClientRect();
            const tr = tEl.getBoundingClientRect();
            // Décalage vertical par ordre de trait (±6 px max) : désolidarise
            // les croisements courts sans décoller des étiquettes
            const off = Math.max(-6, Math.min(6, (order - (traits.length - 1) / 2) * 3));
            segs.push({
                x1: sr.right - cb.left,
                y1: sr.top - cb.top + sr.height / 2 + off,
                x2: tr.left - cb.left,
                y2: tr.top - cb.top + tr.height / 2 + off,
                color,
            });
        });
        setSegments(segs);
    }, [traits]);

    // Recalcul à chaque changement de liens + au redimensionnement (KaTeX, viewport…)
    useLayoutEffect(() => {
        computeSegments();
        const cont = containerRef.current;
        if (!cont || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => computeSegments());
        ro.observe(cont);
        return () => ro.disconnect();
    }, [computeSegments]);

    if (sources.length === 0) return null;

    const normalBtn = clair
        ? 'border-slate-300 bg-white text-slate-800 hover:border-blue-400 hover:scale-[1.01]'
        : 'border-slate-600 bg-slate-700/50 text-slate-200 hover:border-slate-400 hover:scale-[1.01]';
    // En correction : une boîte sans état, figée, sans survol
    const neutreBtn = clair ? 'border-slate-300 bg-white text-slate-800' : 'border-slate-600 bg-slate-700/50 text-slate-200';
    const boiteCorrigee = (etat) => `${etat ? t[etat] : neutreBtn} cursor-default`;

    return (
        <>
            {/* Instructions + compteur de progression (utile avec les cibles
                leurres : des étiquettes restent orphelines même quand c'est fini).
                Masqués en correction : on ne relie plus, on lit. */}
            {etats ? null : (
            <p className={`text-xs sm:text-sm font-medium ${t.instructions} flex items-center gap-2`}>
                <span>🔗</span>
                {selectedSource !== null
                    ? 'Maintenant clique sur l\'élément à relier à droite !'
                    : selectedTarget !== null
                        ? 'Maintenant clique sur l\'élément à relier à gauche !'
                        : 'Clique sur un élément puis sur celui à relier ! (re-clique un élément relié pour le déplacer ou le défaire)'
                }
                <span
                    className={`ml-auto shrink-0 text-[11px] px-2 py-0.5 rounded-full border font-semibold ${
                        Object.keys(links).length === sources.length
                            ? (clair ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-emerald-900/40 text-emerald-300 border-emerald-600')
                            : (clair ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-700/60 text-slate-200 border-slate-500')
                    }`}
                >
                    {Object.keys(links).length}/{sources.length} reliés
                </span>
            </p>
            )}

            {/* Deux colonnes : sources | cibles + overlay des traits */}
            <div ref={containerRef} className="relative flex gap-4 sm:gap-6 mt-3 sm:mt-4">
                {/* Sources — centrées verticalement : avec des cibles leurres la
                    colonne droite est plus haute, les sources se répartissent
                    au centre plutôt que collées en haut (arbitrage UI 2026-07-27) */}
                <div className="flex-1 flex flex-col justify-center gap-2">
                    {sources.map((source, i) => {
                        const isLinked = links[i] !== undefined;
                        const isSelected = selectedSource === i;
                        const e = etats ? etats.get(source) : null;
                        const etatSource = e ? (e.juste ? 'juste' : 'faux') : null;

                        return (
                            <button
                                key={i}
                                type="button"
                                ref={(el) => { sourceRefs.current[i] = el; }}
                                onClick={() => handleSourceClick(i)}
                                disabled={Boolean(etats)}
                                data-etat={etatSource || undefined}
                                className={`relative z-[1] w-full px-3 py-2.5 rounded-xl border-2 text-sm sm:text-base font-medium text-left transition-all duration-300 ${
                                    etats
                                        ? boiteCorrigee(etatSource)
                                        : isSelected
                                            ? `${t.selected} scale-[1.03] shadow-lg shadow-blue-500/20`
                                            : isLinked
                                                ? `${clair ? 'bg-white text-slate-800' : 'bg-slate-800/60 text-slate-200'} cursor-pointer hover:opacity-80`
                                                : normalBtn
                                }`}
                                style={!etats && isLinked && !isSelected ? { borderColor: linkColors.bySource[i] } : undefined}
                            >
                                <MathText>{source}</MathText>
                                {etatSource ? <span aria-hidden="true" className="ml-2 font-black">{marqueSigne(etatSource)}</span> : null}
                            </button>
                        );
                    })}
                </div>

                {/* Cibles mélangées */}
                <div className="flex-1 space-y-2">
                    {shuffledTargets.map((target, i) => {
                        const linkedSourceIdx = getSourceForTarget(i);
                        const isLinked = linkedSourceIdx !== undefined;
                        const isSelected = selectedTarget === i;
                        const c = etatsCibles ? etatsCibles[i] : null;

                        return (
                            <button
                                key={i}
                                type="button"
                                ref={(el) => { targetRefs.current[i] = el; }}
                                onClick={() => handleTargetClick(i)}
                                disabled={Boolean(etats)}
                                data-etat={c?.etat || undefined}
                                className={`relative z-[1] w-full px-3 py-2.5 rounded-xl border-2 text-sm sm:text-base font-medium text-left transition-all duration-300 ${
                                    c
                                        ? boiteCorrigee(c.etat)
                                        : isSelected
                                            ? `${t.selected} scale-[1.03] shadow-lg shadow-blue-500/20`
                                            : isLinked
                                                ? `${clair ? 'bg-white text-slate-800' : 'bg-slate-800/60 text-slate-200'} cursor-pointer hover:opacity-80`
                                                : normalBtn
                                }`}
                                style={!etats && isLinked && !isSelected ? { borderColor: linkColors.byTarget[i] } : undefined}
                            >
                                <MathText>{target}</MathText>
                                {c && (c.etat === 'juste' || c.etat === 'faux') ? <span aria-hidden="true" className="ml-2 font-black">{marqueSigne(c.etat)}</span> : null}
                                {/* la cible attendue nomme sa source, pour qui ne suit pas les traits */}
                                {c ? c.attendPar.map((source) => (
                                    <span key={source} className={`block w-fit mt-1 ${ETIQUETTE_CONTENU}`} data-testid="attend">← <MathText>{source}</MathText></span>
                                )) : null}
                            </button>
                        );
                    })}
                </div>

                {/* Traits de liaison (au-dessus, non cliquables) */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 2 }}>
                    {segments.map((s, i) => {
                        const dx = Math.max(20, Math.abs(s.x2 - s.x1) * 0.4);
                        return (
                            <g key={i}>
                                <path
                                    d={`M ${s.x1} ${s.y1} C ${s.x1 + dx} ${s.y1}, ${s.x2 - dx} ${s.y2}, ${s.x2} ${s.y2}`}
                                    fill="none"
                                    stroke={s.color}
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                />
                                <circle cx={s.x1} cy={s.y1} r="4" fill={s.color} />
                                <circle cx={s.x2} cy={s.y2} r="4" fill={s.color} />
                            </g>
                        );
                    })}
                </svg>
            </div>
        </>
    );
});

export default RelierEleve;
